import { Avatar } from 'components/shared/avatar'
import { useMediaQuery } from 'hooks/use_media_query'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { useSelector } from 'react-redux'
import {
  AudioTrack,
  Participant as TwilioParticipant,
  VideoTrack,
} from 'twilio-video'

import { MEDIA_QUERIES, css, styled } from 'design'
import { Button } from 'primitives/button'
import { Stack } from 'primitives/containers'
import { cn } from 'util/cn'

import { useHandleMetadata } from '../../hooks/use_handle_metadata'
import { makeFetchParticipantByGIDParamSelector } from '../../reduxes/slices/call_participants/selectors'
import { makeGetTopicParticipant } from '../../reduxes/slices/participants/selectors'
import { AudioIndicator } from './tracks/audio_indicator'
import { useAudioTrack } from './tracks/use_audio_track'
import { LocalTrackData } from './tracks/use_local_tracks'
import { useVideoTrack } from './tracks/use_video_track'
import { SCREEN_SHARE_VIDEO_NAME } from './util/web_rtc_config'
import {VideoPinOffIcon, VideoPinOnIcon} from "../../images";

const ParticipantContainer = styled('section', {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  backgroundColor: '$backgroundSecondary',
  borderRadius: '$3',

  '@md': {
    borderRadius: '0',
  },
})

const Video = styled('video', {
  position: 'absolute',
  left: 0,
  top: 0,
  width: '100%',
  maxWidth: '100%',
  maxHeight: '100%',
  borderRadius: '$3',
  pointerEvents: 'none',

  '@md': {
    borderRadius: '0',
  },

  variants: {
    mirror: {
      true: {
        transform: 'scale(-1, 1)',
      },
      false: {},
    },
    videoTrackType: {
      video: {
        height: '100%',
        objectFit: 'contain',
      },
      screenShare: {
        objectFit: 'contain',
        height: 'auto',
        justifySelf: 'center',
        top: '50%',
        transform: 'translateY(-50%)',
      },
    },
  },

  defaultVariants: {
    mirror: 'false',
    videoTrackType: 'video',
  },
})

export type ParticipantProps = {
  participant: TwilioParticipant
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  callRecord?: any
  label?: string
  position?: string
  audioTrack?: AudioTrack
  videoTrack?: VideoTrack
  isAudioDisabled?: boolean
  localTrackData?: LocalTrackData
  setPinnedParticipantId: React.Dispatch<
    React.SetStateAction<string | undefined>
  >
  isPinned?: boolean
  multipleVideos?: boolean
} & React.ComponentProps<typeof ParticipantContainer> &
  React.HTMLAttributes<HTMLDivElement>

export const Participant = ({
  participant,
  callRecord,
  label,
  position,
  className,
  localTrackData,
  setPinnedParticipantId,
  isPinned,
  multipleVideos,
  ...props
}: ParticipantProps) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const publishedAudioTrack = useAudioTrack(participant)
  const publishedVideoTrack = useVideoTrack(participant)
  const publishedScreenShareTrack = useVideoTrack(
    participant,
    SCREEN_SHARE_VIDEO_NAME,
  )
  const {
    unpublishedAudioTrack,
    unpublishedVideoTrack,
    unpublishedScreenShareTrack,
  } = localTrackData || {}
  const audioTrack = publishedAudioTrack || unpublishedAudioTrack
  const videoTrack = publishedVideoTrack || unpublishedVideoTrack
  const screenShareTrack =
    publishedScreenShareTrack || unpublishedScreenShareTrack
  const videoTrackTemp = videoTrack || screenShareTrack
  const videoTrackType = screenShareTrack ? 'screenShare' : 'video'
  const fetchParticipantByGIDParam = useMemo(
    makeFetchParticipantByGIDParamSelector,
    [],
  )
  const participantRecord = useSelector((state) =>
    // eslint-disable-next-line
    // @ts-ignore
    fetchParticipantByGIDParam(state, {
      call: callRecord,
      gidParam: participant?.identity,
    }),
  )
  const getTopicParticipant = useMemo(makeGetTopicParticipant, [])
  const topicParticipant = useSelector((state) =>
    // eslint-disable-next-line
    // @ts-ignore
    getTopicParticipant(state, {
      handle: participantRecord?.handle,
      topicId: callRecord?.topic_id,
    }),
  )
  const { name } = useHandleMetadata(
    participantRecord?.handle,
    topicParticipant,
  )

  useEffect(() => {
    if (unpublishedVideoTrack) {
      // eslint-disable-next-line
      // @ts-ignore
      unpublishedVideoTrack.attach(videoRef.current)
    }
    if (publishedVideoTrack) {
      unpublishedVideoTrack?.detach()
      // eslint-disable-next-line
      // @ts-ignore
      publishedVideoTrack.attach(videoRef.current)
    }
  }, [unpublishedVideoTrack, publishedVideoTrack])

  useEffect(() => {
    if (unpublishedScreenShareTrack) {
      // eslint-disable-next-line
      // @ts-ignore
      unpublishedScreenShareTrack.attach(videoRef.current)
    }
    if (publishedScreenShareTrack) {
      unpublishedScreenShareTrack?.detach()
      // eslint-disable-next-line
      // @ts-ignore
      publishedScreenShareTrack.attach(videoRef.current)
    }
  }, [unpublishedScreenShareTrack, publishedScreenShareTrack])

  useEffect(() => {
    return () => {
      if (isPinned) exitFullscreen()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Empty deps intentionally to only run on dismount

  const desktop = useMediaQuery(MEDIA_QUERIES.md)
  const { videoDevices, videoDeviceId } = localTrackData || {}
  const facingMode =
    videoTrack?.mediaStreamTrack?.getCapabilities?.()?.facingMode
  const isEnvironmentCamera =
    facingMode && facingMode.length
      ? facingMode.includes('environment')
      : !!videoDeviceId &&
        !!videoDevices?.[videoDeviceId].label.toLowerCase().includes('back')
  const mirrorVideo =
    localTrackData && videoTrackType === 'video' && !isEnvironmentCamera

  const goFullscreen = useCallback(() => {
    setPinnedParticipantId(participant.identity)
  }, [participant?.identity, setPinnedParticipantId])

  const exitFullscreen = useCallback(() => {
    setPinnedParticipantId(undefined)
  }, [setPinnedParticipantId])

  const handleClick = useCallback(() => {
    if (!multipleVideos) return
    isPinned ? exitFullscreen() : goFullscreen()
  }, [multipleVideos, exitFullscreen, goFullscreen, isPinned])

  return (
    <ParticipantContainer
      {...props}
      onClick={handleClick}
      className={cn(position && `p${position}`, className)}
    >
      {/* The video element is always muted as all audio is handled by a higher level HTML audio element */}
      {videoTrackTemp && (
        <Video
          mirror={mirrorVideo}
          videoTrackType={videoTrackType}
          ref={videoRef}
          autoPlay
          muted
          disablePictureInPicture
          disableRemotePlayback
          controls={false}
          playsInline
        />
      )}

      {desktop && (
        <Stack
          as="header"
          gap="2"
          direction="row"
          css={{
            position: 'absolute',
            top: '$space$2',
            left: '$space$2',
          }}
        >
          {!videoTrackTemp && (
            <Avatar
              handle={participantRecord?.handle}
              hideOnlineIndicator={true}
            />
          )}
          <Stack
            as="h3"
            gap="1"
            direction="row"
            css={{
              position: 'relative',
              fontSize: '$2',
              padding: '$1 $2',
              color: '$grey950',
              zIndex: 0,
              '&::before': {
                content: '""',
                position: 'absolute',
                left: 0,
                top: 0,
                width: '100%',
                height: '100%',
                borderRadius: '$2',
                opacity: 0.75,
                backgroundColor: '$grey200',
                zIndex: -1,
              },
            }}
          >
            {name && <span>{name}</span>}
            {label && (
              <span className={labelStyles()}>{name ? `${label}` : label}</span>
            )}
            <AudioIndicator
              audioTrack={audioTrack}
              className={audioIndicatorStyles()}
            />

            {multipleVideos &&
              (isPinned ? (
                <Button onClick={exitFullscreen} className={pinIconStyles()}>
                  <VideoPinOnIcon/>
                </Button>
              ) : (
                <Button onClick={goFullscreen} className={pinIconStyles()}>
                  <VideoPinOffIcon/>
                </Button>
              ))}
          </Stack>
        </Stack>
      )}
    </ParticipantContainer>
  )
}

const labelStyles = css({
  opacity: 0.8,
})

const audioIndicatorStyles = css({
  svg: {
    fill: '$foreground',
  },
})

const pinIconStyles = css({
  svg: {
    g: {
      path: {
        fill: '$foreground',
      }
    }
  },
})
